import React, { FC } from 'react';
import { View, Text, TouchableWithoutFeedback } from 'react-native';

import { Element, scale as initialScale } from '@components/frame';
import TouchableElement from '@components/common/touchableElement';
import AttachDetailsModal from './AttachDetailsModal';

import Svg from '@styles/icons/svg';

import { IMAGE_HANDLING_MOBILE_FOOTER_HEIGHT } from '@v2/pages/common/constants/base.constants';
import { ImageHandlingHeaderProps } from '@v2/components/imageHandling/components/Header/Header';
import { ImageHandlingDisplayMode, RetrievedDocumentReferenceLink } from '@v2/components/imageHandling/types/types.d';
import { useManageMobileBottomTabs } from './hooks/useManageMobileBottomTabs';
import InfoPopupTabletMobile from '@v2/components/imageHandling/components/Header/components/InfoPopup/InfoPopupTabletMobile';
import { font as initialFont } from '@styles/fonts';
import RotateModal from '@v2/components/imageHandling/components/Header/components/RotateModal';
import CropperMobile from '@v2/components/imageHandling/components/Cropper/components/CropperMobile';

export interface MobileBottomTabsProps
  extends Pick<
    ImageHandlingHeaderProps,
    | 'C'
    | 'rotateImage'
    | 'cropImage'
    | 'setMode'
    | 'images'
    | 'selectedIndex'
    | 'setSelectedIndex'
    | 'deleteImage'
    | 'font'
    | 'scale'
    | 'triggerDraw'
    | 'isDrawing'
  > {}

const MobileBottomTabs: FC<MobileBottomTabsProps> = ({
  C,
  images,
  rotateImage,
  selectedIndex,
  setSelectedIndex,
  setMode,
  deleteImage,
  cropImage,
  triggerDraw,
  isDrawing,
  font = initialFont,
  scale = initialScale,
}) => {
  const {
    closeAttachModal,
    hoveredStyle,
    img,
    isSingleImage,
    openAttachModal,
    showAttachModal,
    showInfoPopUp,
    showRotatePopUp,
    triggerCrop,
    triggerInfoPopUp,
    triggerRotatePopUp,
    iconContainerSize,
    onSaveCrop,
    showCrop,
    isAnnotationModeEnabled,
  } = useManageMobileBottomTabs({ C, images, selectedIndex, cropImage });

  if (showCrop) {
    return <CropperMobile triggerShow={triggerCrop} onSave={onSaveCrop} image={img} />;
  }

  return (
    <Element
      {...{
        scale,
        config: {
          zIndex: 100,
          position: {
            bottom: 0,
            left: 0,
            right: 0,
          },
          height: IMAGE_HANDLING_MOBILE_FOOTER_HEIGHT,
          backgroundColor: C('Background/Light/menuApp'),
        },
      }}
    >
      <View
        style={{
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          paddingHorizontal: 15,
          paddingTop: 4,
          height: '100%',
        }}
      >
        {/* Clip icon */}
        <View style={{ width: iconContainerSize, height: iconContainerSize }}>
          <TouchableElement onPress={openAttachModal}>
            {({ boxStyle, hovered, pressed }) => (
              <View
                style={[
                  boxStyle,
                  hoveredStyle(hovered, pressed, showAttachModal),
                  {
                    width: iconContainerSize,
                    height: iconContainerSize,
                    justifyContent: 'center',
                    alignItems: 'center',
                    position: 'relative',
                  },
                ]}
              >
                <Svg icon="clip" width={scale(20)} height={scale(20)} color={C('Link/Light/Primary')} />
                {img?.links?.length ? (
                  <Text
                    style={{
                      position: 'absolute',
                      top: scale(22),
                      left: scale(26),
                      color: C('Button/Light/Primary'),
                      fontSize: scale(10),
                    }}
                  >
                    {img?.links?.length ?? 0}
                  </Text>
                ) : null}
              </View>
            )}
          </TouchableElement>
        </View>

        {/* Thumbnail icon - only show if not single image */}
        {!isSingleImage && (
          <View style={{ width: iconContainerSize, height: iconContainerSize }}>
            <TouchableElement
              onPress={() => {
                if (!isSingleImage) {
                  setMode?.(ImageHandlingDisplayMode.THUMBNAIL);
                }
              }}
            >
              {({ boxStyle, hovered, pressed }) => (
                <View
                  style={[
                    boxStyle,
                    hoveredStyle(hovered, pressed),
                    {
                      width: iconContainerSize,
                      height: iconContainerSize,
                      justifyContent: 'center',
                      alignItems: 'center',
                    },
                  ]}
                >
                  <Svg icon="squares" width={scale(20)} height={scale(20)} color={C('Link/Light/Primary')} />
                </View>
              )}
            </TouchableElement>
          </View>
        )}

        {/* Rotate icon */}
        <View style={{ width: iconContainerSize, height: iconContainerSize }}>
          <TouchableElement
            onPress={
              img?.isPDF && img?.pageCount > 1 ? () => triggerRotatePopUp() : () => rotateImage({ documentId: img?.documentId, onePage: true })
            }
          >
            {({ boxStyle, hovered, pressed }) => (
              <View
                style={[
                  boxStyle,
                  hoveredStyle(hovered, pressed, showRotatePopUp),
                  {
                    width: iconContainerSize,
                    height: iconContainerSize,
                    justifyContent: 'center',
                    alignItems: 'center',
                  },
                ]}
              >
                <Svg icon="rotate" width={scale(18)} height={scale(19)} color={C('Link/Light/Primary')} />
              </View>
            )}
          </TouchableElement>
        </View>

        {/* Crop icon */}
        <View style={{ width: iconContainerSize, height: iconContainerSize }}>
          <TouchableElement onPress={!img?.isProcessing ? () => triggerCrop() : () => {}}>
            {({ boxStyle, hovered, pressed }) => (
              <View
                style={[
                  boxStyle,
                  hoveredStyle(hovered, pressed),
                  {
                    width: iconContainerSize,
                    height: iconContainerSize,
                    justifyContent: 'center',
                    alignItems: 'center',
                  },
                ]}
              >
                <Svg icon="crop" width={scale(21)} height={scale(21)} color={C('Link/Light/Primary')} />
              </View>
            )}
          </TouchableElement>
        </View>

        {/* Draw / annotations icon */}
        {isAnnotationModeEnabled && (
          <View style={{ width: iconContainerSize, height: iconContainerSize }}>
            <TouchableElement onPress={triggerDraw ?? (() => {})}>
              {({ boxStyle, hovered, pressed }) => (
                <View
                  style={[
                    boxStyle,
                    hoveredStyle(hovered, pressed, !!isDrawing),
                    {
                      width: iconContainerSize,
                      height: iconContainerSize,
                      justifyContent: 'center',
                      alignItems: 'center',
                    },
                  ]}
                >
                  <Svg icon="annotations" width={scale(20)} height={scale(20)} color={C('Link/Light/Primary')} />
                </View>
              )}
            </TouchableElement>
          </View>
        )}

        {/* Info icon */}
        <View style={{ width: iconContainerSize, height: iconContainerSize }}>
          <TouchableElement onPress={triggerInfoPopUp}>
            {({ boxStyle, hovered, pressed }) => (
              <View
                style={[
                  boxStyle,
                  hoveredStyle(hovered, pressed, showInfoPopUp),
                  {
                    width: iconContainerSize,
                    height: iconContainerSize,
                    justifyContent: 'center',
                    alignItems: 'center',
                  },
                ]}
              >
                <Svg icon="more" width={scale(20)} height={scale(20)} color={'Button/Light/Primary'} />
              </View>
            )}
          </TouchableElement>
        </View>
      </View>
      {showAttachModal && (
        <>
          {/* overlay */}
          <TouchableWithoutFeedback onPress={closeAttachModal}>
            <View
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'transparent',
                zIndex: 5000,
              }}
            />
          </TouchableWithoutFeedback>
          <AttachDetailsModal
            {...{
              images,
              imageId: img?.documentId,
              fileName: img?.fileName,
              C,
              visible: true,
              onClose: closeAttachModal,
              links: (img?.links || []) as RetrievedDocumentReferenceLink[],
              img,
              title: img?.fileName,
              documentType: img?.documentTag,
            }}
          />
        </>
      )}
      <InfoPopupTabletMobile
        {...{
          C,
          deleteImage,
          font,
          scale,
          images,
          onClose: triggerInfoPopUp,
          selectedIndex,
          setSelectedIndex,
          visible: showInfoPopUp,
        }}
      />
      <RotateModal
        C={C}
        rotateImage={rotateImage}
        showRotatePopUp={showRotatePopUp}
        triggerRotatePopUp={triggerRotatePopUp}
        images={images}
        selectedIndex={selectedIndex}
      />
    </Element>
  );
};

export default MobileBottomTabs;
